import { useCallback, useMemo } from 'react';

import { Prettify } from '@givebacks/utils/types';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  FormsMethodAtom,
  formByIdAtom,
  isAllFormsValidAtom,
  isAnyFormDirtyAtom,
  validateAllFormsAsyncAtom,
} from '../atoms';
import { BasicForm } from '../types/form';
import { ScrollParams } from './scrolling/scrolling.types';
import { useScrollToError } from './scrolling/useScrollToError';
import { useScrollToForm } from './scrolling/useScrollToForm';
import { useSelectionValidationForm } from './selectors/useSelectionValidationForm';
import { useFormyContext } from './useFormyContext';

/**
 * Get a form by id
 *
 * @param id - The id of the form to get
 * @returns The form
 */
const useGetFormById = <TForms extends BasicForm, K extends keyof TForms>() => {
  const { store } = useFormyContext();
  const data = useSetAtom(formByIdAtom, { store });
  return data as unknown as (id: K) => TForms[K];
};

const useValidateForms = () => {
  const { store } = useFormyContext();
  const isFormsValid = useAtomValue(isAllFormsValidAtom, { store });
  const validateAllForms = useSetAtom(FormsMethodAtom, { store });
  return useCallback(() => {
    validateAllForms('validate');
    return { isValid: isFormsValid };
  }, [isFormsValid, validateAllForms]);
};

/**
 *
 * Validates all forms and returns a promise with the results
 *
 * Combines the results of all forms and returns the results
 *
 * @returns A promise with the results of all forms
 */
const useValidateFormsAsync = () => {
  const { store } = useFormyContext();
  const validateAllForms = useSetAtom(validateAllFormsAsyncAtom, { store });

  return useCallback(async () => {
    return await validateAllForms();
  }, [validateAllForms]);
};

const useIsFormsDirty = () => {
  const { store } = useFormyContext();
  const dirtyForms = useAtomValue(isAnyFormDirtyAtom, { store });
  return useMemo(() => dirtyForms, [dirtyForms]);
};

function useFormyValidator<TForms extends BasicForm>() {
  const { store } = useFormyContext();
  const callFormMethod = useSetAtom(FormsMethodAtom, { store });
  const validateForms = useValidateForms();
  const validateFormsAsync = useValidateFormsAsync();
  const isFormsValid = useAtomValue(isAllFormsValidAtom, { store });

  const useGetForm = <K extends keyof TForms>(select?: K) =>
    useSelectionValidationForm<TForms, K>(select) as TForms[K];

  const useFormScroll = <K extends keyof TForms>(formId: K, scrollParams?: ScrollParams) =>
    useScrollToForm<TForms, K, TForms[K]>(formId, scrollParams);

  const useGetFormById_ = () => useGetFormById<TForms, keyof TForms>();

  return {
    isFormsValid: useMemo(() => isFormsValid, [isFormsValid]),
    validateForms,
    validateFormsAsync,
    resetForms: useCallback(() => callFormMethod('reset'), [callFormMethod]),
    resetDirtyForms: useCallback(() => callFormMethod('resetDirty'), [callFormMethod]),
    useGetForms: () => useSelectionValidationForm<TForms, keyof TForms>() as Prettify<TForms>,
    useIsFormsDirty: () => useIsFormsDirty(),
    useGetFormById: useGetFormById_,
    useFormScroll,
    useScrollToError,
    useGetForm,
  };
}

export { useFormyValidator };
